import { Select } from '../../../../../components/inputs/Select';
import { ApiError } from '../../../../../lib/apiError';
import {
  CITIZENSHIP_LABELS,
  DUAL_CITIZENSHIP_KIND_LABELS,
  type Citizenship as CitizenshipValue,
  type DualCitizenshipKind,
  type PersonnelCatalogEntry,
} from '../../personnelTypes';
/**
 * The PDS citizenship question, and the two that follow only from one of its answers.
 *
 * Shared by the create wizard and the Personal editor, so a new hire can state their
 * citizenship on the form that records them rather than only on a later correction — and so
 * the two forms cannot drift into asking it differently.
 *
 * **The follow-ups appear rather than sit there greyed out.** A reader who is not a dual
 * citizen is not being asked which country they were naturalized in — a disabled pair of
 * fields is a form asking a question it has just been told does not apply.
 */
export function CitizenshipFields(props: {
  citizenship: CitizenshipValue | null;
  setCitizenship: (next: CitizenshipValue | null) => void;
  kind: DualCitizenshipKind | null;
  setKind: (next: DualCitizenshipKind | null) => void;
  countryId: string | null;
  setCountryId: (next: string | null) => void;
  countries: PersonnelCatalogEntry[];
  countryName: string | null;

  /*
    Passed rather than read from the section-form context, because this is used by the
    create WIZARD too and the wizard has no such context. A shared field group that can
    only live inside one shell is a shared field group in name only.
  */
  saving: boolean;
  error: ApiError | null;
}) {
  const { saving, error } = props;

  const isDual = props.citizenship === 2;

  /*
    The stored country is injected as an option so the picker states its value on the first
    paint — the catalog arrives a moment later, and an id with no matching option renders as
    the placeholder, which reads as an empty field on a record that has one.
  */
  const countryOptions = props.countries.map((c) => ({ value: c.id, label: c.name }));

  if (props.countryId && !props.countries.some((c) => c.id === props.countryId)) {
    countryOptions.unshift({ value: props.countryId, label: props.countryName ?? '…' });
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Select
        id="personnel-personal-citizenship"
        label="Citizenship"
        value={props.citizenship === null ? '' : String(props.citizenship)}
        onChange={(next) => {
          const value = next ? (Number(next) as CitizenshipValue) : null;

          props.setCitizenship(value);

          // Leaving Dual drops its follow-ups rather than keeping them behind a hidden
          // section — a stale answer nothing on screen accounts for.
          if (value !== 2) {
            props.setKind(null);
            props.setCountryId(null);
          }
        }}
        options={Object.entries(CITIZENSHIP_LABELS).map(([value, label]) => ({ value, label }))}
        sort="none"
        error={error?.fieldError('citizenship')}
        disabled={saving}
      />

      {isDual ? (
        <Select
          id="personnel-personal-dual-citizenship-kind"
          label="Dual citizenship"
          required
          value={props.kind === null ? '' : String(props.kind)}
          onChange={(next) => props.setKind(next ? (Number(next) as DualCitizenshipKind) : null)}
          options={Object.entries(DUAL_CITIZENSHIP_KIND_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
          sort="none"
          error={
            props.kind === null
              ? 'Say whether it is by birth or by naturalization.'
              : error?.fieldError('dualCitizenshipKind')
          }
          disabled={saving}
        />
      ) : null}

      {isDual ? (
        <div className="sm:col-span-2">
          <Select
            id="personnel-personal-dual-citizenship-country-id"
            label="Country"
            required
            description="For holders of foreign/dual citizenship, please select from the dropdown list the foreign country where you were born/naturalized."
            value={props.countryId}
            onChange={props.setCountryId}
            options={countryOptions}
            error={
              props.countryId === null
                ? 'Choose the country.'
                : error?.fieldError('dualCitizenshipCountryId')
            }
            disabled={saving}
          />
        </div>
      ) : null}
    </div>
  );
}
